import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dealCurrencyCode", "paymentLinks", "hiddenIds", "placeholder", "noResultsText", "disabledOptions", "disabledOptionsReason"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Link } from 'foundations-components/transitional/components/Link';
import Raven from 'raven-js';
import FormattedPrice from './FormattedPrice';
import RecurringMessage from './RecurringMessage';
import { getPaymentLinksUrl } from '../utils';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function PaymentLinkHeader({
  paymentLink: {
    id,
    name,
    subtotals
  }
}) {
  const {
    firstPaymentTotal,
    currencyCode,
    recurringSubtotals
  } = subtotals || {};
  const hasRecurringSubtotal = recurringSubtotals && recurringSubtotals[0];
  return /*#__PURE__*/_jsx(TruncateString, {
    title: name,
    "data-selenium": `payment-value-${id}`,
    "data-test-id": "payment-link-header",
    useFlex: true,
    fixedChildren: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      inlineSize: "auto",
      children: [/*#__PURE__*/_jsx(View, {
        marginInline: "100",
        as: "span",
        children: "|"
      }), /*#__PURE__*/_jsx(FormattedPrice, {
        currencyCode: currencyCode,
        price: firstPaymentTotal
      }), hasRecurringSubtotal && /*#__PURE__*/_jsx(Small, {
        use: "help",
        marginInlineStart: "100",
        children: /*#__PURE__*/_jsx(TruncateString, {
          maxWidth: 125,
          children: /*#__PURE__*/_jsx(RecurringMessage, {
            currencyCode: currencyCode,
            recurringSubtotals: recurringSubtotals
          })
        })
      })]
    }),
    children: name
  });
}
function PaymentLinkOption({
  option,
  children
}) {
  const {
    disabled,
    disabledOptionsReason,
    paymentLink
  } = option;
  if (!paymentLink) return children;
  return /*#__PURE__*/_jsx(PaymentLinkItem, {
    disabled: disabled,
    disabledOptionsReason: disabledOptionsReason,
    paymentLink: paymentLink
  });
}
export function PaymentLinkItem({
  disabled = false,
  disabledOptionsReason,
  paymentLink
}) {
  const {
    lineItems = []
  } = paymentLink;
  const products = lineItems.map(item => item.name).join(', ');
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !disabled || !disabledOptionsReason,
    title: disabledOptionsReason,
    children: /*#__PURE__*/_jsxs(View, {
      "data-selenium": `payment-option-${paymentLink.id}`,
      marginInline: "500",
      paddingBlock: "200",
      as: "div",
      children: [/*#__PURE__*/_jsx(PaymentLinkHeader, {
        paymentLink: paymentLink
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(TruncateString, {
          tooltip: products,
          children: /*#__PURE__*/_jsx(Small, {
            disabled: disabled,
            use: "help",
            children: products
          })
        })
      })]
    })
  });
}
function ValueComponent({
  option,
  children
}) {
  const {
    paymentLink
  } = option;
  // handles the case where the option is the placeholder, which is not a payment link
  if (!paymentLink) return children;
  return /*#__PURE__*/_jsx(PaymentLinkHeader, {
    paymentLink: paymentLink
  });
}
function PaymentLinkSelect(_ref) {
  let {
      dealCurrencyCode,
      paymentLinks,
      hiddenIds,
      placeholder,
      noResultsText,
      disabledOptions = false,
      disabledOptionsReason
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  let options = [];
  const ON = 'ON';
  try {
    options = paymentLinks.filter(paymentLink => {
      if (dealCurrencyCode) {
        return paymentLink.state === ON && paymentLink.currency === dealCurrencyCode;
      }
      return paymentLink.state === ON;
    }).map(paymentLink => {
      return {
        value: paymentLink.id,
        text: paymentLink.name,
        paymentLink,
        disabled: disabledOptions,
        disabledOptionsReason
      };
    });
    if (hiddenIds && hiddenIds.length) {
      options = options.filter(option => !hiddenIds.includes(option.value));
    }
  } catch (error) {
    // TODO add error state handling
    Raven.captureMessage('Bad paymentLinks prop, not in right format', {
      extra: {
        paymentLinks
      }
    });
  }
  return /*#__PURE__*/_jsx(Select, Object.assign({
    "data-selenium": "payment-link-select",
    options: options,
    filterOptions: filterOptions,
    optionComponent: PaymentLinkOption,
    valueComponent: ValueComponent,
    noResultsText: noResultsText || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "paymentLinkSelect.noResults"
    }),
    placeholder: placeholder || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "paymentLinkSelect.placeholder"
    }),
    minimumSearchCount: 0,
    dropdownFooter: /*#__PURE__*/_jsx(Link, {
      href: getPaymentLinksUrl(),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "paymentLinkSelect.goToPaymentLinks"
      })
    })
  }, props));
}

// When search term is entered, look for matching payment link name or line item name
function filterOptions(options, rawInput) {
  const input = rawInput.toLowerCase();
  return options.filter(option => {
    const {
      text,
      paymentLink
    } = option;
    if (text && text.toLowerCase().includes(input)) return true;
    if (paymentLink && Array.isArray(paymentLink.lineItems)) {
      return paymentLink.lineItems.some(item => item.name && item.name.toLowerCase().includes(input));
    }
    return false;
  });
}
export default PaymentLinkSelect;